import React, { useState } from 'react';
import { FiMapPin, FiX } from 'react-icons/fi';
import { parseCoordinateInput, fromStatePlane } from '../../utils/coordinateUtils';

function GpsEntryModal({ onClose, onDropPin }) {
  const [mode, setMode] = useState('latlng'); // 'latlng' or 'stateplane'
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [northingInput, setNorthingInput] = useState('');
  const [eastingInput, setEastingInput] = useState('');
  const [zone, setZone] = useState('west');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    setError('');

    if (mode === 'latlng') {
      const lat = parseCoordinateInput(latInput);
      const lng = parseCoordinateInput(lngInput);

      if (!lat.valid) {
        setError('Invalid latitude. Use decimal (34.0522) or DMS (34 3 7.92 N)');
        return;
      }
      if (!lng.valid) {
        setError('Invalid longitude. Use decimal (-84.5) or DMS (84 30 0 W)');
        return;
      }

      // Validate Georgia range
      if (lat.value < 30.3 || lat.value > 35.0) {
        setError('Latitude out of Georgia range (30.3 - 35.0)');
        return;
      }

      let lngVal = lng.value;
      // If user entered positive longitude for west, make it negative
      if (lngVal > 0 && lngVal > 60) lngVal = -lngVal;

      if (lngVal > -80.8 || lngVal < -85.6) {
        setError('Longitude out of Georgia range (-80.8 to -85.6)');
        return;
      }

      onDropPin(lat.value, lngVal, label || `GPS Point`);
    } else {
      // State Plane mode
      const northing = parseFloat(northingInput);
      const easting = parseFloat(eastingInput);

      if (isNaN(northing) || northing <= 0) {
        setError('Invalid Northing value');
        return;
      }
      if (isNaN(easting) || easting <= 0) {
        setError('Invalid Easting value');
        return;
      }

      try {
        const { lat, lng } = fromStatePlane(northing, easting, zone);

        // Validate result is in Georgia
        if (lat < 30.3 || lat > 35.0 || lng > -80.8 || lng < -85.6) {
          setError('Coordinates are outside Georgia. Check values and zone.');
          return;
        }

        onDropPin(lat, lng, label || `GPS Point`);
      } catch (e) {
        setError('Failed to convert coordinates. Check values and zone.');
      }
    }
  };

  const tabStyle = (isActive) => ({
    flex: 1,
    padding: '10px',
    backgroundColor: isActive ? '#2563eb' : '#f1f5f9',
    color: isActive ? 'white' : '#374151',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    borderRadius: isActive ? '6px' : '6px'
  });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'monospace',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 100000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiMapPin /> Drop Pin at Coordinates
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
          >
            <FiX />
          </button>
        </div>

        {/* Mode Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '12px 20px 0' }}>
          <button onClick={() => setMode('latlng')} style={tabStyle(mode === 'latlng')}>
            Lat / Lng
          </button>
          <button onClick={() => setMode('stateplane')} style={tabStyle(mode === 'stateplane')}>
            State Plane
          </button>
        </div>

        {/* Form */}
        <div style={{ padding: '16px 20px' }}>
          {mode === 'latlng' ? (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Latitude</label>
                <input
                  type="text"
                  value={latInput}
                  onChange={(e) => setLatInput(e.target.value)}
                  placeholder="34.0522 or 34 3 7.92 N"
                  style={inputStyle}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Longitude</label>
                <input
                  type="text"
                  value={lngInput}
                  onChange={(e) => setLngInput(e.target.value)}
                  placeholder="-84.5 or 84 30 0 W"
                  style={inputStyle}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Zone</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setZone('west')}
                    style={{
                      ...tabStyle(zone === 'west'),
                      padding: '8px 16px'
                    }}
                  >
                    GA West (2240)
                  </button>
                  <button
                    onClick={() => setZone('east')}
                    style={{
                      ...tabStyle(zone === 'east'),
                      padding: '8px 16px'
                    }}
                  >
                    GA East (2239)
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Northing (ft)</label>
                <input
                  type="text"
                  value={northingInput}
                  onChange={(e) => setNorthingInput(e.target.value)}
                  placeholder="e.g., 1598234.56"
                  style={inputStyle}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Easting (ft)</label>
                <input
                  type="text"
                  value={eastingInput}
                  onChange={(e) => setEastingInput(e.target.value)}
                  placeholder="e.g., 823456.78"
                  style={inputStyle}
                />
              </div>
            </>
          )}

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., NW Corner"
              style={{ ...inputStyle, fontFamily: 'inherit' }}
            />
          </div>

          {error && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              borderRadius: '6px',
              fontSize: '13px',
              marginBottom: '12px'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 16px',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FiMapPin /> Drop Pin
          </button>
        </div>
      </div>
    </div>
  );
}

export default GpsEntryModal;
